import { requestStatisticsRefresh } from '@brands/store/slices/conversationStatisticsSlice';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import useTwilioConversationsClient from './conversation-hooks/useTwilioConversationsClient';

// A burst of messages (e.g., a participant pasting a long thread) only
// needs one stats refetch to re-derive the unread counts. 300ms coalesces
// rapid-fire arrivals without introducing a user-visible lag on the
// common "single new message" case.
const MESSAGE_ADDED_DEBOUNCE_MS = 300;

/**
 * DO-3975: keeps the conversation-statistics slice fresh against the
 * Twilio Conversations Client's real-time events, so the Messages
 * sidebar red-dot badge reflects incoming messages without a page
 * refresh. Mount once, from an always-mounted component (Sidenav).
 *
 * Three independent signals converge on a single `requestStatisticsRefresh`
 * dispatch, each compensating for the others' blind spots:
 *
 * 1. `client.messageAdded` — fires as soon as Twilio delivers a new
 *    message in any conversation the user participates in. This is the
 *    primary path for instant badge updates. Debounced to collapse
 *    rapid bursts into a single refetch.
 *
 * 2. `client.connectionStateChanged → 'connected'` — covers the case
 *    where messages arrived while the Twilio transport was
 *    disconnected (network blip, token renewal via `useJwtExpired`,
 *    etc.). `messageAdded` can't fire during the offline window, so we
 *    rely on the reconnect edge to force a catch-up refetch. Not
 *    debounced — reconnect should clear stale state immediately.
 *
 * 3. `document.visibilitychange → 'visible'` — fallback for long
 *    backgrounded sessions where the OS/browser may have paused the
 *    WebSocket and Twilio hasn't yet re-established the connection by
 *    the time the user refocuses the tab. Cheap insurance.
 */
export const useConversationStatisticsRealtime = (): void => {
  const client = useTwilioConversationsClient();
  const dispatch = useDispatch();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const refresh = (): void => {
      dispatch(requestStatisticsRefresh());
    };

    const scheduleDebouncedRefresh = (): void => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        refresh();
      }, MESSAGE_ADDED_DEBOUNCE_MS);
    };

    const onMessageAdded = (): void => {
      scheduleDebouncedRefresh();
    };

    const onConnectionStateChanged = (state: string): void => {
      if (state === 'connected') {
        refresh();
      }
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // The Twilio Client is initialized asynchronously by
    // `useTwilioConversationsClient`, so `client` may be null on the
    // first render. This effect re-runs once the client becomes
    // available, at which point the listeners attach.
    if (client) {
      client.on('messageAdded', onMessageAdded);
      client.on('connectionStateChanged', onConnectionStateChanged);
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (client) {
        client.off('messageAdded', onMessageAdded);
        client.off('connectionStateChanged', onConnectionStateChanged);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [client, dispatch]);
};

export default useConversationStatisticsRealtime;
